import {
  type AnyCatalogue,
  DEFAULT_LOCALE,
  FALLBACK_CHAIN,
  type Locale,
  type MessageValue,
  type PluralForms,
} from './types';

/**
 * The locale-independent resolution machinery: fallback chain walk, plural-form
 * selection through `Intl.PluralRules`, and `{placeholder}` interpolation.
 *
 * It lives apart from `translate.ts` because two callers need exactly this and
 * must not drift: the platform catalogue (`t`) and app-local catalogues built
 * with `createCatalog()`. One implementation means an app-local string falls back
 * to English by the same rule the platform strings do (PRD NFR-006).
 */

const pluralRules = new Map<Locale, Intl.PluralRules>();

function rulesFor(locale: Locale): Intl.PluralRules {
  let rules = pluralRules.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRules.set(locale, rules);
  }
  return rules;
}

export function isPluralForms(value: MessageValue): value is PluralForms {
  return typeof value !== 'string';
}

export function selectPluralForm(locale: Locale, forms: PluralForms, count: number): string {
  const category = rulesFor(locale).select(count) as keyof PluralForms;
  return forms[category] ?? forms.other;
}

export function interpolate(
  template: string,
  params: Readonly<Record<string, string | number>>,
): string {
  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/** Walks the locale's fallback chain, then English, and returns the first hit. */
export function lookupIn<TSource extends AnyCatalogue>(
  catalogues: Readonly<Record<Locale, Readonly<Record<keyof TSource, MessageValue>>>>,
  locale: Locale,
  key: keyof TSource,
): MessageValue | undefined {
  for (const candidate of FALLBACK_CHAIN[locale]) {
    const value = catalogues[candidate][key];
    if (value !== undefined) return value;
  }
  return catalogues[DEFAULT_LOCALE][key];
}

/**
 * Resolve one key to a finished string. A key missing from every locale is a
 * programming error, not a runtime condition - the catalogue types and the
 * completeness test both prevent it - so it degrades to the key itself rather
 * than blanking a screen during a morning service.
 */
export function resolveMessage<TSource extends AnyCatalogue>(
  catalogues: Readonly<Record<Locale, Readonly<Record<keyof TSource, MessageValue>>>>,
  locale: Locale,
  key: keyof TSource,
  params?: Readonly<Record<string, string | number>>,
): string {
  const value = lookupIn<TSource>(catalogues, locale, key);
  if (value === undefined) return String(key);

  const resolved = isPluralForms(value)
    ? selectPluralForm(locale, value, Number(params?.count ?? 0))
    : value;

  return params ? interpolate(resolved, params) : resolved;
}

/** Every `{placeholder}` a catalogue value uses, across all of its plural forms. */
export function placeholdersOf(value: MessageValue): Set<string> {
  const templates = typeof value === 'string' ? [value] : Object.values(value);
  const found = new Set<string>();
  for (const template of templates) {
    if (typeof template !== 'string') continue;
    for (const match of template.matchAll(/\{([A-Za-z0-9_]+)\}/g)) {
      const name = match[1];
      if (name) found.add(name);
    }
  }
  return found;
}
